import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import type { Snippet } from '@snippits/shared';
import { getSnippet, getSnippetImageUrl, updateSnippetTitle } from '../lib/snippets';
import { useAuth } from '../lib/useAuth';

export default function Viewer() {
  const { shortId } = useParams<{ shortId: string }>();
  const [params] = useSearchParams();
  const auth = useAuth();

  const [snippet, setSnippet] = useState<Snippet | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(
    params.get('justCreated') === '1' ? 'Link copied to clipboard' : null,
  );

  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 2500);
    return () => window.clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    if (!shortId) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getSnippet(shortId);
        if (cancelled) return;
        if (!snap) {
          setError('not-found');
          return;
        }
        setSnippet(snap);
        const url = await getSnippetImageUrl(snap);
        if (!cancelled) setImageUrl(url);
      } catch {
        if (!cancelled) setError('load-failed');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [shortId]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href.split('?')[0]);
      setToast('Link copied');
    } catch {
      setToast('Could not copy — select the URL instead');
    }
  };

  const isOwner =
    snippet != null && auth.status === 'signed-in' && auth.user.uid === snippet.ownerUid;

  const onTitleCommit = async (newTitle: string) => {
    if (!snippet) return;
    if (newTitle.trim() === snippet.title) return;
    const optimistic = { ...snippet, title: newTitle.trim() || 'Untitled' };
    setSnippet(optimistic);
    try {
      await updateSnippetTitle(snippet.id, newTitle);
    } catch {
      setSnippet(snippet);
      setToast("Couldn't save title");
    }
  };

  if (error === 'not-found') {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 bg-neutral-900 p-6 text-center">
        <h1 className="text-2xl font-semibold">Snippet not found</h1>
        <p className="text-sm text-neutral-400">It may have been deleted.</p>
      </div>
    );
  }

  if (error === 'load-failed') {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 bg-neutral-900 p-6 text-center">
        <h1 className="text-2xl font-semibold">Couldn&apos;t load this snippet</h1>
        <p className="text-sm text-neutral-400">Try refreshing.</p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col bg-neutral-900">
      <div className="flex items-center justify-between gap-3 border-b border-neutral-800 px-4 py-2">
        <div className="min-w-0 flex-1">
          {snippet ? (
            <TitleView
              title={snippet.title}
              editable={isOwner}
              onCommit={onTitleCommit}
            />
          ) : (
            <span className="text-sm text-neutral-400">Loading…</span>
          )}
          {snippet && (
            <div className="text-[11px] text-neutral-500">
              {snippet.width} × {snippet.height}
            </div>
          )}
        </div>
        <div className="flex shrink-0 gap-2">
          {imageUrl && (
            <a
              href={imageUrl}
              download={`${shortId}.png`}
              className="rounded-md border border-neutral-800 px-3 py-1.5 text-sm hover:border-neutral-700 hover:bg-neutral-800"
            >
              Download
            </a>
          )}
          <button
            onClick={copyLink}
            className="rounded-md bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600"
          >
            Copy link
          </button>
        </div>
      </div>

      <div className="flex flex-1 items-center justify-center p-6">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={snippet?.title ?? 'Snippet'}
            className="h-auto w-auto max-h-[85vh] max-w-full rounded-md border border-neutral-800 object-contain shadow-2xl"
            width={snippet?.width}
            height={snippet?.height}
          />
        ) : (
          <p className="text-sm text-neutral-400">Loading…</p>
        )}
      </div>

      {toast && (
        <div className="pointer-events-none fixed bottom-6 left-1/2 -translate-x-1/2 rounded-md bg-neutral-100 px-4 py-2 text-sm text-neutral-900 shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
}

function TitleView({
  title,
  editable,
  onCommit,
}: {
  title: string;
  editable: boolean;
  onCommit: (next: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(title);

  useEffect(() => {
    setValue(title);
  }, [title]);

  if (editing) {
    return (
      <input
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={() => {
          setEditing(false);
          onCommit(value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            (e.target as HTMLInputElement).blur();
          } else if (e.key === 'Escape') {
            setValue(title);
            setEditing(false);
          }
        }}
        className="w-full rounded border border-neutral-700 bg-neutral-800 px-2 py-0.5 text-sm font-medium outline-none focus:border-brand-500"
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => editable && setEditing(true)}
      disabled={!editable}
      title={editable ? 'Click to rename' : undefined}
      className={`truncate text-left text-sm font-medium ${
        editable ? 'hover:text-brand-500' : 'cursor-default'
      }`}
    >
      {title}
    </button>
  );
}
